import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/shared/api/client';
import { CONNECTEE } from '@/shared/utils/liveness';

/**
 * Vérifie qu'un actif a RÉELLEMENT suivi l'ordre qu'on vient de lui envoyer.
 *
 * Une réponse HTTP 200 dit seulement que le backend a publié sur le broker. Elle ne dit
 * pas que la batterie a changé d'état : le device peut être plein, vide, hors ligne, ou
 * simplement lent. Tant qu'on n'a pas relu sa télémétrie, on ne sait rien — et laisser
 * croire le contraire sur un écran qui pilote l'installation d'un client serait pire que
 * de ne rien afficher.
 *
 * Extrait de `confirmBatteryState`, qui vivait dans MonitoringPage : la boucle existait
 * en un seul exemplaire, enfermée dans un toast, et l'écran de pilotage n'y avait pas
 * accès.
 */

const POLL_INTERVAL_MS = 2000;
// 60 s. Les 16 s d'origine étaient calées sur un essai favorable : le 27/07, le
// HiBattery a mis plus de trois minutes à refléter une consigne. Une fenêtre trop
// courte ne rend pas un verdict, elle rend un faux négatif.
const POLL_ATTEMPTS = 30;
export const POLL_WINDOW_S = (POLL_INTERVAL_MS * POLL_ATTEMPTS) / 1000;
const SETTLE_DELAY_MS = 3000;     // relecture unique pour les ordres sans état attendu

/**
 * Signature d'état attendue après une commande batterie.
 * `null` = aucun état ne permet de trancher : on relit une fois, sans rendre de verdict.
 */
function expectedState(batteryAction) {
  if (batteryAction === 'charge') return { etat: 'charge', sign: -1 };
  if (batteryAction === 'decharge') return { etat: 'decharge', sign: 1 };
  // auto / power_zero / reboot : « veille » et 0 W sont aussi l'état d'une batterie
  // qui n'a rien reçu. Confirmer sur cette base reviendrait à valider n'importe quoi.
  return null;
}

const matches = (data, want) =>
  data?.etat === want.etat && Math.sign(data.puissance_kw || 0) === want.sign;

/**
 * La machine est-elle hors d'atteinte ?
 *
 * On ne se fie qu'à `etat_connexion`, le champ précis. Absent (types d'actifs sans relevé
 * de fraîcheur), on ne conclut rien : mieux vaut attendre la fenêtre complète que
 * d'annoncer « injoignable » sur une simple absence d'information.
 *
 * Conséquence : la boucle s'arrête là. Attendre soixante secondes une machine qui ne
 * parle plus ne produit aucune information nouvelle, seulement un doute plus long.
 */
const injoignable = (data) => {
  const etat = data?.etat_connexion;
  return !!etat && etat !== CONNECTEE;
};

/**
 * @param {function} onRead (assetId, asset) — appelé à CHAQUE relecture réussie.
 *   La boucle interroge déjà l'API toutes les 2 s : sans ce rappel, la donnée fraîche
 *   restait enfermée dans la popup pendant que la ligne derrière affichait du périmé,
 *   et il fallait recharger la page pour la voir bouger.
 */
export default function useCommandConfirmation(onRead) {
  // idle | pending | confirmed | unconfirmed | unreachable | skipped
  const [phase, setPhase] = useState('idle');
  const [telemetry, setTelemetry] = useState(null);
  // Incrémenté à chaque nouvelle vérification et au démontage : une boucle dont le
  // jeton a changé s'arrête sans écrire. Sans ça, fermer la popup avant la fin des
  // 16 s laisse un setTimeout écrire dans un composant démonté.
  const runId = useRef(0);
  // Par référence : la boucle est lancée une fois et dure une minute ; capturer le
  // callback dans une closure figerait la version du rendu qui a déclenché l'ordre.
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;

  useEffect(() => () => { runId.current += 1; }, []);

  const reset = useCallback(() => {
    runId.current += 1;
    setPhase('idle');
    setTelemetry(null);
  }, []);

  const cancel = reset;

  const wait = (ms, token) => new Promise(resolve => setTimeout(
    () => resolve(runId.current === token), ms));

  const readAsset = async (assetId) => {
    try {
      const { data } = await api.get(`/monitoring/assets/${assetId}`);
      onReadRef.current?.(assetId, data);
      return data;
    } catch {
      return null;   // relecture ratée : on retente au tour suivant
    }
  };

  /**
   * Lance la vérification. Ne lève jamais : une vérification qui échoue est une
   * information à afficher, pas une erreur à propager.
   *
   * @param {string} assetId
   * @param {string} batteryAction code matériel (charge | decharge | auto | power_zero | reboot)
   * @param {boolean} verifiable false pour les types d'actifs sans relecture d'état
   */
  const verify = useCallback(async (assetId, batteryAction, verifiable = true) => {
    runId.current += 1;
    const token = runId.current;

    if (!verifiable || !assetId) {
      setPhase('skipped');
      return 'skipped';
    }

    setPhase('pending');
    setTelemetry(null);
    const want = expectedState(batteryAction);

    if (!want) {
      if (!(await wait(SETTLE_DELAY_MS, token))) return null;
      const data = await readAsset(assetId);
      if (runId.current !== token) return null;
      setTelemetry(data);
      // Même sans état à confirmer, « la machine ne répond plus » est le verdict le plus
      // utile qu'on puisse rendre : il vaut mieux que « pas de relecture possible ».
      const verdict = injoignable(data) ? 'unreachable' : 'skipped';
      setPhase(verdict);
      return verdict;
    }

    for (let i = 0; i < POLL_ATTEMPTS; i += 1) {
      if (!(await wait(POLL_INTERVAL_MS, token))) return null;
      const data = await readAsset(assetId);
      if (runId.current !== token) return null;
      if (data) setTelemetry(data);
      if (data && matches(data, want)) {
        setPhase('confirmed');
        return 'confirmed';
      }
      // Machine injoignable : on ne joue pas les 60 s restantes, il n'y a personne au
      // bout du fil. Le verdict honnête est disponible tout de suite.
      if (injoignable(data)) {
        setPhase('unreachable');
        return 'unreachable';
      }
    }

    setPhase('unconfirmed');
    return 'unconfirmed';
  }, []);

  return { phase, telemetry, verify, reset, cancel };
}
